import { useQueries } from '@tanstack/react-query'
import PaginatedResponse from '@/types/paginated-response'
import { BaseUser } from '@/types/users'
import axios from '../auth-axios'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import { filtersToQueryString } from '../request-wrappers'

export function useGetAuthUser({
  id,
  ...options
}: UseGetDetailOptions<BaseUser>) {
  const query = useGetDetail<BaseUser>({
    id,
    queryKey: ['auth-user', id, options.filters],
    serviceFn: ({ filters, axiosOptions }) => {
      const searchParams = filtersToQueryString(filters)
      return axios.get<BaseUser>(
        `/v2/auth/users/${id}?${searchParams}`,
        axiosOptions,
      )
    },
    ...options,
  })

  return {
    user: query.data,
    ...query,
  }
}

/**
 * Fetches multiple auth users in parallel by id. Each query shares the
 * `auth-user` detail cache key so previously-fetched users resolve instantly.
 */
export function useGetAuthUsers(ids: string[]) {
  const uniqueIds = [...new Set(ids)].filter(Boolean)

  return useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['auth-user', id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        axios
          .get<BaseUser>(`/v2/auth/users/${id}?`, { signal })
          .then((res) => res.data),
    })),
    combine: (results) => {
      const usersById: Record<string, BaseUser> = {}
      results.forEach((result, index) => {
        if (result.data) usersById[uniqueIds[index]] = result.data
      })
      return {
        usersById,
        isLoading: results.some((result) => result.isPending),
      }
    },
  })
}

export type Role = {
  id: string
  description: string
  order: number
  is_assignable: boolean
}

/**
 * MARK: Custom Hook: Get Auth Roles
 * This hook fetches the list of roles from the auth service.
 */
export function useGetAuthRoles(options: UseGetListOptions<Role> = {}) {
  const query = useGetList<Role>({
    queryKey: ['auth-roles', options.filters],
    serviceFn: ({ filters, axiosOptions }) => {
      const searchParams = filtersToQueryString(filters)
      return axios.get<PaginatedResponse<Role>>(
        `/v2/auth/roles?${searchParams}`,
        axiosOptions,
      )
    },
    ...options,
  })

  return {
    roles: query.allData || [],
    ...query,
  }
}

type UpdateUser = {
  id: string // can be stytch member_id or external_id/pd_user_id
  roles_ids?: string[]
  employee_ext_id?: string
}

/**
 * MARK: Action Hook: Update Auth User
 * This hook provides a mutation function to update the roles assigned to a user or their employee external ID.
 */
export function useUpdateAuthUser(
  options: Omit<UseUpdateDetailOptions<UpdateUser>, 'filters'> = {},
) {
  const mutation = useUpdateDetail<UpdateUser>({
    serviceFn: ({ item, axiosOptions }) => {
      return axios.patch<UpdateUser>(
        `/v2/auth/users/${item.id}`,
        item,
        axiosOptions,
      )
    },
    ...options,
  })

  return {
    updateAuthUser: mutation.mutate,
    updateAuthUserAsync: mutation.mutateAsync,
    ...mutation,
  }
}
